/**
 * Basic Field Components
 * 
 * These are the simple form fields that don't require complex data dependencies.
 * Each field is rendered EXACTLY as in App.js to maintain visual parity.
 * 
 * Supports two modes:
 * - 'edit': Interactive inputs (default)
 * - 'view': Styled read-only display with pill styling for selections
 * 
 * IMPORTANT: Uses DebouncedInput and DebouncedTextarea to prevent UI "jumps"
 * during typing. These components maintain local state and only propagate
 * changes to the parent after a delay.
 */

import React, { useCallback, useRef } from 'react';
import axios from 'axios';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import { DebouncedInput, DebouncedTextarea } from '../../../ui/debounced-textarea';
import { Label } from '../../../ui/label';
import { Button } from '../../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { ChevronDown } from 'lucide-react';
import { FormFileUploadField } from '../../../shared/FormFileUploadField';
import { formatDateMDY } from '../../../../utils/dateFormat';

const FILE_API = process.env.REACT_APP_BACKEND_URL;

/**
 * Styled display value for view mode
 */
const ViewValue = ({ children, className = '' }) => (
  <div className={`text-sm text-gray-800 py-1 ${className}`}>
    {children || <span className="text-gray-400 italic">Not provided</span>}
  </div>
);

/**
 * Pill-styled value for selections in view mode
 */
const ViewPill = ({ children, variant = 'default' }) => {
  const variants = {
    default: 'bg-blue-100 text-blue-800 border-blue-200',
    selected: 'bg-blue-600 text-white border-blue-600',
    neutral: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${variants[variant]}`}>
      {children}
    </span>
  );
};

/**
 * Render a basic field based on its type
 * Returns null if field type is not a basic field
 */
export const renderBasicField = (field, index, fieldStyle, context) => {
  const { newNoteFormData = {}, handleFieldChange, disabled, mode = 'edit' } = context;
  const isViewMode = mode === 'view';
  
  // Helper to handle field changes
  const onChange = (value) => {
    void 0 && console.log('[basicField onChange] field:', field.id, 'value:', String(value).substring(0, 30));
    if (handleFieldChange) {
      if (typeof handleFieldChange === 'function') {
        // If it's a direct handler function
        handleFieldChange(prev => ({ ...prev, [field.id]: value }));
      }
    }
  };
  
  // Get value - check newNoteFormData first, then field.value (for saved notes)
  const getValue = () => {
    const formValue = newNoteFormData[field.id];
    if (formValue !== undefined && formValue !== null && formValue !== '') {
      return formValue;
    }
    return field.value ?? '';
  };
  
  const value = getValue();
  
  // Text input - uses DebouncedInput to prevent UI jumps
  if (field.type === 'text') {
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        {isViewMode ? (
          <ViewValue>{value}</ViewValue>
        ) : (
          <DebouncedInput 
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label?.toLowerCase() || ''}`}
            disabled={disabled}
            debounceMs={150}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
        )}
      </div>
    );
  }
  
  // Email input - uses DebouncedInput to prevent UI jumps
  if (field.type === 'email') {
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        {isViewMode ? (
          <ViewValue>{value}</ViewValue>
        ) : (
          <DebouncedInput 
            type="email"
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label?.toLowerCase() || ''}`}
            disabled={disabled}
            debounceMs={150}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
        )}
      </div>
    );
  }
  
  // Phone input - uses DebouncedInput to prevent UI jumps
  if (field.type === 'phone') {
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        {isViewMode ? (
          <ViewValue>{value}</ViewValue>
        ) : (
          <DebouncedInput 
            type="tel"
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label?.toLowerCase() || ''}`}
            disabled={disabled}
            debounceMs={150}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
        )}
      </div>
    );
  }
  
  // Date input
  if (field.type === 'date') {
    // Parse date without timezone conversion — numeric M/D/YYYY (Jun 2026)
    const displayDate = value ? formatDateMDY(value) : '';
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        {isViewMode ? (
          <ViewValue>{displayDate}</ViewValue>
        ) : (
          <Input 
            type="date"
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
        )}
      </div>
    );
  }
  
  // Time input
  if (field.type === 'number') {
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        {isViewMode ? (
          <ViewValue>{value === '' || value === null || value === undefined ? '' : String(value)}</ViewValue>
        ) : (
          <Input 
            type="number"
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder={field.placeholder || ''}
            data-testid={`number-field-${field.id}`}
          />
        )}
      </div>
    );
  }
  
  if (field.type === 'rating') {
    const rating = parseInt(value) || 0;
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        <div className="flex items-center gap-1 mt-1" data-testid={`rating-field-${field.id}`}>
          {[1, 2, 3, 4, 5].map((star) => (
            isViewMode ? (
              <span key={star} className={`text-2xl ${rating >= star ? 'text-yellow-400' : 'text-gray-300'}`}>★</span>
            ) : (
              <button
                key={star}
                type="button"
                onClick={() => onChange(star)}
                disabled={disabled}
                className={`text-2xl ${rating >= star ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
              >
                ★
              </button>
            )
          ))}
          <span className="ml-2 text-sm text-gray-500">
            {rating ? `${rating}/5` : (isViewMode ? 'Not rated' : 'Not rated')}
          </span>
        </div>
      </div>
    );
  }
  
  if (field.type === 'time') {
    // Format time for display (convert 24h to 12h format)
    let displayTime = '';
    if (value) {
      const match = String(value).match(/^(\d{2}):(\d{2})/);
      if (match) {
        const [, hours, minutes] = match;
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        displayTime = `${hour12}:${minutes} ${ampm}`;
      } else {
        displayTime = value;
      }
    }
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        {isViewMode ? (
          <ViewValue>{displayTime}</ViewValue>
        ) : (
          <Input 
            type="time"
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
        )}
      </div>
    );
  }
  
  // Textarea - uses DebouncedTextarea to prevent UI jumps
  if (field.type === 'textarea') {
    const rowCount = parseInt(field.rows) || 2;
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        {isViewMode ? (
          <div className="text-sm text-gray-800 whitespace-pre-wrap py-1">
            {value || <span className="text-gray-400 italic">Not provided</span>}
          </div>
        ) : (
          <DebouncedTextarea 
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label?.toLowerCase() || ''}`}
            rows={rowCount}
            disabled={disabled}
            debounceMs={150}
            autoGrow={true}
            className={`flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y overflow-auto ${rowCount > 1 ? 'min-h-[60px]' : ''}`}
          />
        )}
      </div>
    );
  }
  
  // Select dropdown
  if (field.type === 'select') {
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        {isViewMode ? (
          value ? <ViewPill>{value}</ViewPill> : <ViewValue />
        ) : (
          <Select 
            value={value} 
            onValueChange={(val) => onChange(val)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Select ${field.label?.toLowerCase() || ''}`} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((option, idx) => (
                <SelectItem key={idx} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    );
  }
  
  // Radio buttons (pill style - matches App.js exactly)
  if (field.type === 'radio') {
    // Handle multi-select mode
    const isMultiSelect = field.allowMultiple === true;
    const currentValues = isMultiSelect 
      ? (Array.isArray(value) ? value.map(String) : (value ? [String(value)] : []))
      : [];
    
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        {isViewMode ? (
          // View mode: Show ALL options with selected ones highlighted
          <div className="flex flex-wrap gap-2 mt-1">
            {(field.options || []).length > 0 ? (
              (field.options || []).map((option, idx) => {
                const optValue = String(typeof option === 'object' ? (option.value || option.label) : option);
                const optLabel = typeof option === 'object' ? option.label : option;
                const isSelected = isMultiSelect 
                  ? currentValues.includes(optValue)
                  : String(value) === optValue;
                return (
                  <span 
                    key={idx}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                      isSelected 
                        ? 'bg-blue-600 text-white border-blue-600' 
                        : 'bg-gray-100 text-gray-500 border-gray-200'
                    }`}
                  >
                    {optLabel}
                  </span>
                );
              })
            ) : (
              <span className="text-gray-400 italic text-sm">No options</span>
            )}
          </div>
        ) : (
          // Edit mode
          <div className="flex flex-wrap gap-2 mt-1">
            {(field.options || []).map((option, idx) => {
              const optValue = String(typeof option === 'object' ? (option.value || option.label) : option);
              const optLabel = typeof option === 'object' ? option.label : option;
              const isSelected = isMultiSelect 
                ? currentValues.includes(optValue)
                : String(value) === optValue;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    if (disabled) return;
                    if (isMultiSelect) {
                      const newValues = isSelected 
                        ? currentValues.filter(v => v !== optValue)
                        : [...currentValues, optValue];
                      onChange(newValues);
                    } else {
                      onChange(optValue);
                    }
                  }}
                  disabled={disabled}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                    isSelected 
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {optLabel}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  
  // Single checkbox
  if (field.type === 'checkbox') {
    const isChecked = value === true || value === 'true';
    return (
      <div key={index} style={{...fieldStyle, overflowWrap: 'break-word', maxWidth: '100%'}}>
        {isViewMode ? (
          <div className="flex items-start space-x-2">
            <span className={`inline-flex items-center justify-center w-4 h-4 rounded border text-xs flex-shrink-0 mt-0.5 ${
              isChecked ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-gray-300'
            }`}>
              {isChecked && '✓'}
            </span>
            <span className="text-sm text-gray-800 break-words" style={{wordBreak: 'break-word'}}>
              {field.label}
            </span>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <input 
              type="checkbox" 
              id={`checkbox-${field.id}`}
              checked={isChecked}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <Label htmlFor={`checkbox-${field.id}`} className="text-sm cursor-pointer">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
          </div>
        )}
      </div>
    );
  }
  
  // Checkbox group
  if (field.type === 'checkbox_group') {
    const currentValues = Array.isArray(value) ? value : [];
    
    // Determine columns - but force single column if field width is narrow (25% or less)
    const getColumnClass = () => {
      // Check if field is in a narrow column (3 columns or more in a row = 33% or less width each)
      const fieldWidth = field.width || field.columnSpan || 12;
      const isNarrow = fieldWidth <= 4; // 4/12 = 33% or less
      
      if (isNarrow) return 'grid-cols-1'; // Force single column for narrow fields
      
      const cols = field.checkboxColumns || field.columns || '1';
      if (cols === '3' || cols === 3) return 'grid-cols-3';
      if (cols === '2' || cols === 2) return 'grid-cols-2';
      return 'grid-cols-1';
    };
    
    return (
      <div key={index} style={{...fieldStyle, minWidth: 0}} className="checkbox-group-container">
        <Label className="block mb-1 font-medium text-sm" style={{ wordBreak: 'break-word' }}>
          {field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}
        </Label>
        <div className="border rounded p-2 bg-gray-50" style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {isViewMode ? (
            // View mode: Show ALL options as checkboxes, checked ones with teal checkmark
            <div className={`grid gap-1.5 ${getColumnClass()}`}>
              {(field.options || []).map((option, idx) => {
                const isChecked = currentValues.includes(option);
                return (
                  <div key={idx} className="flex items-start space-x-2 min-w-0">
                    <span className={`inline-flex items-center justify-center w-4 h-4 rounded border text-xs flex-shrink-0 mt-0.5 ${
                      isChecked 
                        ? 'bg-teal-600 border-teal-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}>
                      {isChecked && '✓'}
                    </span>
                    <span className="text-sm text-gray-800 leading-tight break-words" style={{wordBreak: 'break-word'}}>
                      {option}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={`grid gap-1.5 ${getColumnClass()}`}>
              {(field.options || []).map((option, idx) => (
                <div key={idx} className="flex items-start space-x-2 min-w-0">
                  <input 
                    type="checkbox" 
                    id={`${field.id}-${idx}`} 
                    checked={currentValues.includes(option)}
                    onChange={(e) => {
                      let newValues = [...currentValues];
                      if (e.target.checked) {
                        newValues.push(option);
                      } else {
                        newValues = newValues.filter(v => v !== option);
                      }
                      onChange(newValues);
                    }}
                    disabled={disabled}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 flex-shrink-0 mt-0.5"
                  />
                  <label 
                    htmlFor={`${field.id}-${idx}`} 
                    className="text-sm font-normal cursor-pointer leading-relaxed"
                    style={{ wordBreak: 'break-word' }}
                  >
                    {option}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Multiselect - using Popover like live App.js
  if (field.type === 'multiselect' || field.type === 'multi-select') {
    const currentValues = Array.isArray(value) ? value : [];
    const options = field.options || [];
    
    if (options.length === 0 && !isViewMode) {
      return (
        <div key={index} style={fieldStyle}>
          <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
          <div className="text-gray-400 text-sm italic">No options configured</div>
        </div>
      );
    }
    
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        {isViewMode ? (
          <div className="flex flex-wrap gap-2 mt-1">
            {currentValues.length > 0 ? (
              currentValues.map((val, idx) => (
                <ViewPill key={idx}>{val}</ViewPill>
              ))
            ) : (
              <span className="text-gray-400 italic text-sm">None selected</span>
            )}
          </div>
        ) : (
          <div className="relative">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between text-left font-normal h-auto min-h-[40px] py-2"
                  disabled={disabled}
                >
                  <span className="flex flex-wrap gap-1">
                    {currentValues.length === 0 ? (
                      <span className="text-gray-500">Select options...</span>
                    ) : currentValues.length <= 3 ? (
                      currentValues.map((val, i) => (
                        <span key={i} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                          {val}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-700">{currentValues.length} selected</span>
                    )}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0 max-h-[300px] overflow-y-auto" align="start">
                <div className="p-2 space-y-1">
                  {options.map((option, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                      onClick={() => {
                        if (disabled) return;
                        let newValues = [...currentValues];
                        if (newValues.includes(option)) {
                          newValues = newValues.filter(v => v !== option);
                        } else {
                          newValues.push(option);
                        }
                        onChange(newValues);
                      }}
                    >
                      <input 
                        type="checkbox" 
                        checked={currentValues.includes(option)}
                        onChange={() => {}}
                        className="h-4 w-4"
                        disabled={disabled}
                      />
                      <span className="text-sm">{option}</span>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>
    );
  }
  
  // Provider name (the NOTE's provider + credentials, not the logged-in user)
  if (field.type === 'current_provider_name') {
    const { user } = context;
    const prov = context.noteProvider || user;
    const provDisplay = prov ? `${prov.full_name || 'N/A'}${prov.credentials ? `, ${prov.credentials}` : ''}` : 'N/A';
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{provDisplay}</ViewValue>
        ) : (
          <Input 
            value={provDisplay} 
            disabled 
            className="bg-gray-100"
          />
        )}
      </div>
    );
  }
  
  // Current logged-in user (distinct from the note's provider)
  if (field.type === 'current_user_name') {
    const { user } = context;
    const userDisplay = user ? `${user.full_name || 'N/A'}${user.credentials ? `, ${user.credentials}` : ''}` : 'N/A';
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{userDisplay}</ViewValue>
        ) : (
          <Input value={userDisplay} disabled className="bg-gray-100" />
        )}
      </div>
    );
  }
  
  // Current date (read-only in both modes)
  if (field.type === 'current_date') {
    const dateStr = new Date().toLocaleDateString('en-US');
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label}</Label>
        {isViewMode ? (
          <ViewValue>{dateStr}</ViewValue>
        ) : (
          <Input 
            value={dateStr} 
            disabled 
            className="bg-gray-100"
          />
        )}
      </div>
    );
  }
  
  // File upload field (Jun 2026) — stores {document_id, filename}; the file
  // itself is saved into the client's chart documents.
  if (field.type === 'file') {
    const fileClientId = context.selectedClient?.id
      || (Array.isArray(context.selectedNoteClients) ? context.selectedNoteClients[0] : null);
    const viewUploaded = async (info) => {
      try {
        const r = await axios.get(`${FILE_API}/api/documents/${info.document_id}/download?inline=true`, { responseType: 'blob' });
        const ct = r.headers['content-type'] || '';
        if (ct.includes('application/json')) {
          const payload = JSON.parse(await r.data.text());
          if (payload?.download_url) { window.open(payload.download_url, '_blank'); return; }
        }
        window.open(window.URL.createObjectURL(new Blob([r.data], { type: ct || 'application/pdf' })), '_blank');
      } catch { /* toast handled globally */ }
    };
    return (
      <div key={index} style={fieldStyle}>
        <Label>{field.label} {field.required && !isViewMode && <span className="text-red-500">*</span>}</Label>
        <div className="mt-1">
          <FormFileUploadField
            value={value}
            disabled={disabled || isViewMode || !fileClientId}
            testId={`file-field-${field.id}`}
            onView={viewUploaded}
            onChange={(v) => onChange(v)}
            uploadFile={async (file) => {
              const fd = new FormData();
              fd.append('file', file);
              fd.append('description', `Uploaded via form field "${field.label || field.id}"`);
              const r = await axios.post(`${FILE_API}/api/clients/${fileClientId}/documents`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
              });
              return { document_id: r.data?.id, filename: r.data?.filename || file.name };
            }}
          />
        </div>
      </div>
    );
  }

  // Not a basic field type
  return null;
};

export default renderBasicField;
